// UI
import { Heading } from "@/components/Typography"
import { CardBody, CardWrapper } from "@/components/Card"
import {
	Table,
	TableBody,
	TableDataCell,
	TableHead,
	TableHeading,
	TableRowCell,
} from "@/components/table-controls/TableItems"

// i18n
import { useTrans } from "@/i18n"
import { useLang } from "@/context/lang"

// Queries
import { useMemberGetMemberConversionsQuery } from "@/api/graphql"

// Table
import { useMemo, Fragment } from "react"
import {
	getCoreRowModel,
	useReactTable,
	createColumnHelper,
	flexRender,
} from "@/lib/table"

type AvailableClaim = {
	id: string
	amount: string
	code: string
}

const columnHelper = createColumnHelper<AvailableClaim>()

/**
 * AvailableMemberGetMemberConversions
 * @returns
 */
export function AvailableMemberGetMemberConversions() {
	const { data } = useMemberGetMemberConversionsQuery()

	// Translations
	const t = useTrans("investor")
	const { formatCurrency } = useLang()

	// Table columns
	const columns = useMemo(
		() => [
			columnHelper.accessor("code", {
				header: () => (
					<TableHeading variant="static">
						{t("investor.community.invites.table.code")}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell>{info.getValue()}</TableDataCell>
				),
			}),
			columnHelper.accessor("amount", {
				header: () => (
					<TableHeading variant="static">
						{t("investor:investor.community.invites.table.amount")}
					</TableHeading>
				),
				cell: (info) => (
					<TableDataCell>
						{formatCurrency(Number(info.getValue()), {
							maximumFractionDigits: 0,
						})}
					</TableDataCell>
				),
			}),
		],
		[t, formatCurrency],
	)

	// Initialize table
	const table = useReactTable({
		columns,
		data: (data as AvailableClaim[]) ?? [],
		getCoreRowModel: getCoreRowModel(),
	})

	// Do not show this table if we don't have conversions yet
	if (data?.me?.member_get_member_conversions?.length === 0) {
		return null
	}

	return (
		<CardWrapper className="mt-8">
			<CardBody>
				<Heading as="h3" styleAs="h6" className="mb-4">
					{t("investor.community.invites.earned_codes_heading")}
				</Heading>
				<Table>
					<TableHead>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRowCell key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<Fragment key={header.id}>
										{flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
									</Fragment>
								))}
							</TableRowCell>
						))}
					</TableHead>
					<TableBody>
						{table.getRowModel().rows.map((row, index) => (
							<TableRowCell key={row.id} isOdd={index % 2 === 0}>
								{row.getAllCells().map((cell) => (
									<Fragment key={cell.id}>
										{flexRender(
											cell.column.columnDef.cell,
											cell.getContext(),
										)}
									</Fragment>
								))}
							</TableRowCell>
						))}
					</TableBody>
				</Table>
			</CardBody>
		</CardWrapper>
	)
}
